import { useEffect, useState } from 'react';
import { createSearchIndex, filterAllReleaseNotesBySearchResults, mapReleaseNotesContentItemMapToSearchDocumentList } from '@/pages/release-notes/model/search-engine';
import { useReleaseNotesItemsSelector } from '@/store/selectors';
/**
 * Manages client-side full-text search over the cached release notes.
 *
 * - Rebuilds the search index whenever the Redux cache changes.
 * - Returns new (non-mutated) objects from the filter; highlighted content
 *   and highlight flags live on the local `filteredItems` copies only.
 * - Manages accordion open/collapsed state.
 */
export const useReleaseNotesSearch = () => {
    const storedItems = useReleaseNotesItemsSelector();
    const [searchValue, setSearchValue] = useState('');
    const [filteredItems, setFilteredItems] = useState([]);
    const [openState, setOpenState] = useState({});
    // Rebuild filtered list whenever the Redux cache is updated (new notes downloaded).
    useEffect(() => {
        if (!storedItems.allIds.length)
            return;
        const search = createSearchIndex(mapReleaseNotesContentItemMapToSearchDocumentList(storedItems));
        setFilteredItems(filterAllReleaseNotesBySearchResults(storedItems, '', search));
    }, [storedItems]);
    // Sync open/collapsed state whenever the filtered list changes.
    useEffect(() => {
        setOpenState(filteredItems.reduce((acc, item, index) => {
            acc[item.fileName] = !!searchValue || index === 0;
            return acc;
        }, {}));
        // searchValue intentionally excluded: open state should only reset when filteredItems change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredItems]);
    const handleSearch = () => {
        const search = createSearchIndex(mapReleaseNotesContentItemMapToSearchDocumentList(storedItems));
        setFilteredItems(filterAllReleaseNotesBySearchResults(storedItems, searchValue, search));
    };
    const toggleAccordion = (fileName) => {
        setOpenState((prev) => (Object.assign(Object.assign({}, prev), { [fileName]: !prev[fileName] })));
    };
    return {
        searchValue,
        setSearchValue,
        filteredItems,
        openState,
        handleSearch,
        toggleAccordion,
    };
};
